import React, { useCallback, useEffect, useState, useMemo } from 'react';
import {
  Box,
  Flex,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Checkbox,
} from '@chakra-ui/react';
import { getAccountingDataApi } from 'api/bankingApi';
import OverlaySpinnerWrapper from 'components/OverlaySpinner';
import Card from 'components/card/Card';
import FieldInput from 'components/fields/FieldInput';
import { TwDatePicker } from 'components/DatePicker';
import { getDefaultAccountingEndDate } from 'helpers/dateHelper';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { ChevronDownIcon, ChevronRightIcon, DownloadIcon, TriangleUpIcon, TriangleDownIcon, MinusIcon } from '@chakra-ui/icons';

/**
 * Formats a numeric value as USD currency
 * @param {number} value - The numeric value to format
 * @returns {string} Formatted currency string with negative values in parentheses
 * @example
 * formatCurrency(1234.56) // "$1,235"
 * formatCurrency(-1234.56) // "($1,235)"
 */
const formatCurrency = (value) => {
  if (value == null || Number.isNaN(value)) return '–';
  const isNegative = value < 0;
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(value));
  return isNegative ? `(${formatted})` : formatted;
};

/**
 * Calculates the percentage change between two values
 * @param {number} current - The current value
 * @param {number} previous - The previous value to compare against
 * @returns {number|null} Percentage change or null if calculation isn't possible
 * @example
 * calculateChange(150, 100) // 50 (50% increase)
 * calculateChange(75, 100) // -25 (25% decrease)
 */
const calculateChange = (current, previous) => {
  if (previous === 0 || previous == null || current == null) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
};

/**
 * Displays a visual indicator for percentage change with color coding
 * @param {Object} props
 * @param {number|null} props.change - The percentage change to display
 * @returns {JSX.Element|null} Color-coded change indicator with icon and percentage
 */
const ChangeIndicator = ({ change }) => {
  if (change === null) return null;
  
  const isPositive = change > 0;
  const isNegative = change < 0;
  const isZero = change === 0;
  
  return (
    <Flex align="center" justify="flex-end" gap={1} fontSize="sm" fontWeight="medium">
      {isPositive && <TriangleUpIcon w={3} h={3} color="green.500" />}
      {isNegative && <TriangleDownIcon w={3} h={3} color="red.500" />}
      {isZero && <MinusIcon w={3} h={3} color="gray.400" />}
      <Text color={isPositive ? 'green.500' : isNegative ? 'red.500' : 'gray.400'}>
        {Math.abs(change).toFixed(1)}%
      </Text>
    </Flex>
  );
};

/**
 * Renders a collapsible financial table for P&L or Balance Sheet data
 * @param {Object} props
 * @param {string} props.title - Table title to display in header
 * @param {Array} props.sections - Array of financial sections with accounts and values
 * @param {Array<string>} props.monthLabels - Array of month labels for column headers
 * @param {Array} props.totals - Array of total values to display at bottom
 * @param {Set} props.expandedSections - Set of expanded section IDs
 * @param {Function} props.onToggleSection - Callback to toggle section expansion
 * @param {Set} props.expandedSubsections - Set of expanded subsection IDs (Balance Sheet only)
 * @param {Function} props.onToggleSubsection - Callback to toggle subsection expansion (Balance Sheet only)
 * @param {boolean} props.isPnl - Whether this is a P&L table (affects rendering logic)
 * @returns {JSX.Element} Rendered financial table with expand/collapse functionality
 */
const FinancialTable = ({ title, sections, monthLabels, totals, expandedSections, onToggleSection, expandedSubsections, onToggleSubsection, isPnl }) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headerBg = useColorModeValue('gray.50', 'gray.700');
  const sectionBg = useColorModeValue('gray.100', 'gray.600');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const primaryColor = useColorModeValue('blue.600', 'blue.300');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const totalRowBg = useColorModeValue('blue.50', 'blue.900');
  const subsectionBg = useColorModeValue('gray.50', 'gray.700');

  // Derive header labels from actual data so column count matches body (fixes "vs Prev" misalignment when selected months > data months)
  // Reverse the order so oldest month is first (leftmost)
  const displayMonthLabels = useMemo(() => {
    const firstWithMonths = sections?.find((s) => s.total_months?.length > 0);
    const months = firstWithMonths?.total_months ?? totals?.gross_profit ?? totals?.net_profit ?? totals?.total_assets ?? [];
    if (months.length === 0) return monthLabels;
    return months.map((m) => (moment(m.month).isValid() ? moment(m.month).format('MMM YYYY') : m.month)).reverse();
  }, [sections, totals, monthLabels]);

  const showChange = displayMonthLabels.length > 1;

  return (
    <Card bg={bgColor} borderWidth="1px" borderColor={borderColor} borderRadius="lg" overflow="hidden">
      <Box px={5} py={4} borderBottom="1px" borderColor={borderColor}>
        <Text fontWeight="semibold" fontSize="md">{title}</Text>
      </Box>
      <TableContainer overflowX="auto" maxW="100%">
        <Table variant="simple" size="sm">
          <Thead bg={headerBg}>
            <Tr>
              <Th
                textTransform="uppercase"
                fontSize="xs"
                fontWeight="medium"
                color={mutedColor}
                minW="200px"
              >
                Item
              </Th>
              {displayMonthLabels.map((month, idx) => (
                <Th
                  key={month}
                  isNumeric
                  textTransform="uppercase"
                  fontSize="xs"
                  fontWeight="medium"
                  color={idx === displayMonthLabels.length - 1 ? primaryColor : mutedColor}
                  minW="120px"
                >
                  {month}
                </Th>
              ))}
              {showChange && (
                <Th
                  isNumeric
                  textTransform="uppercase"
                  fontSize="xs"
                  fontWeight="medium"
                  color={mutedColor}
                  minW="100px"
                >
                  vs Prev
                </Th>
              )}
            </Tr>
          </Thead>
          <Tbody>
            {sections?.map((section, sectionIdx) => {
              // Parse section_class from id (format: "class:section_id")
              const [sectionClass, sectionId] = section.id?.includes(':') 
                ? section.id.split(':') 
                : ['', section.id];
              const displayId = sectionId || section.id;
              
              const isExpanded = expandedSections.has(section.id);
              // Reverse total_months array for display (oldest first)
              const reversedTotalMonths = section.total_months ? [...section.total_months].reverse() : [];
              // Calculate change: compare most recent (now last) vs previous (second-to-last)
              const sectionChange =
                showChange && reversedTotalMonths.length >= 2
                  ? calculateChange(reversedTotalMonths[reversedTotalMonths.length - 1].value, reversedTotalMonths[reversedTotalMonths.length - 2].value)
                  : null;
              
              const hasSubsections = section.subsections && section.subsections.length > 0;
              const hasAccounts = section.accounts && section.accounts.length > 0;

              // Check if this is the last section of its class
              const nextSection = sections[sectionIdx + 1];
              const nextSectionClass = nextSection?.id?.includes(':') 
                ? nextSection.id.split(':')[0] 
                : '';
              const isLastOfClass = !nextSection || nextSectionClass !== sectionClass;

              return (
                <React.Fragment key={section.id}>
                  {/* Section Header Row */}
                  <Tr
                    bg={sectionBg}
                    cursor="pointer"
                    _hover={{ bg: hoverBg }}
                    onClick={() => onToggleSection(section.id)}
                  >
                    <Td fontWeight="semibold" fontSize="sm">
                      <Flex align="center" gap={2}>
                        {isExpanded ? (
                          <ChevronDownIcon w={4} h={4} color={mutedColor} />
                        ) : (
                          <ChevronRightIcon w={4} h={4} color={mutedColor} />
                        )}
                        {section.label}
                      </Flex>
                    </Td>
                    {reversedTotalMonths.map((monthData, mIdx) => (
                      <Td
                        key={monthData.month}
                        isNumeric
                        fontWeight="semibold"
                        fontSize="sm"
                        color={monthData.value < 0 ? 'red.500' : mIdx === reversedTotalMonths.length - 1 ? 'inherit' : mutedColor}
                      >
                        {formatCurrency(monthData.value)}
                      </Td>
                    ))}
                    {showChange && (
                      <Td isNumeric>
                        <ChangeIndicator change={sectionChange} />
                      </Td>
                    )}
                  </Tr>

                  {/* Subsections (for Balance Sheet) or Direct Accounts (for P&L) */}
                  {isExpanded && (
                    <>
                      {/* If subsections exist (Balance Sheet hierarchical structure) */}
                      {hasSubsections && section.subsections.map((subsection, subIdx) => {
                        const subsectionKey = `${section.id}:${subsection.id}`;
                        const isSubsectionExpanded = expandedSubsections?.has(subsectionKey);
                        // Reverse subsection months for display (oldest first)
                        const reversedSubsectionMonths = subsection.total_months ? [...subsection.total_months].reverse() : [];
                        const subsectionChange =
                          showChange && reversedSubsectionMonths.length >= 2
                            ? calculateChange(reversedSubsectionMonths[reversedSubsectionMonths.length - 1].value, reversedSubsectionMonths[reversedSubsectionMonths.length - 2].value)
                            : null;

                        return (
                          <React.Fragment key={`${section.id}-${subsection.id}-${subIdx}`}>
                            {/* Subsection Total Row - Clickable */}
                            <Tr 
                              bg={subsectionBg} 
                              fontWeight="medium"
                              cursor="pointer"
                              _hover={{ bg: hoverBg }}
                              onClick={() => onToggleSubsection && onToggleSubsection(section.id, subsection.id)}
                            >
                              <Td fontSize="sm" pl={8}>
                                <Flex align="center" gap={2}>
                                  {isSubsectionExpanded ? (
                                    <ChevronDownIcon w={3} h={3} color={mutedColor} />
                                  ) : (
                                    <ChevronRightIcon w={3} h={3} color={mutedColor} />
                                  )}
                                  {subsection.label}
                                </Flex>
                              </Td>
                              {reversedSubsectionMonths.map((monthData, mIdx) => (
                                <Td
                                  key={monthData.month}
                                  isNumeric
                                  fontSize="sm"
                                  fontWeight="medium"
                                  color={monthData.value < 0 ? 'red.500' : mIdx === reversedSubsectionMonths.length - 1 ? 'inherit' : mutedColor}
                                >
                                  {formatCurrency(monthData.value)}
                                </Td>
                              ))}
                              {showChange && (
                                <Td isNumeric>
                                  <ChangeIndicator change={subsectionChange} />
                                </Td>
                              )}
                            </Tr>
                            {/* Accounts under this subsection - only show when expanded */}
                            {isSubsectionExpanded && subsection.accounts?.map((account, accountIdx) => {
                              // Reverse account months for display (oldest first)
                              const reversedAccountMonths = account.months ? [...account.months].reverse() : [];
                              const accountChange =
                                showChange && reversedAccountMonths.length >= 2
                                  ? calculateChange(reversedAccountMonths[reversedAccountMonths.length - 1].value, reversedAccountMonths[reversedAccountMonths.length - 2].value)
                                  : null;

                              return (
                                <Tr
                                  key={`${section.id}-${subsection.id}-${account.label}-${accountIdx}`}
                                  _hover={{ bg: hoverBg }}
                                >
                                  <Td fontSize="sm" pl={14}>
                                    {account.label}
                                  </Td>
                                  {reversedAccountMonths.map((monthData, mIdx) => (
                                    <Td
                                      key={monthData.month}
                                      isNumeric
                                      fontSize="sm"
                                      color={monthData.value < 0 ? 'red.500' : mIdx === reversedAccountMonths.length - 1 ? 'inherit' : mutedColor}
                                    >
                                      {formatCurrency(monthData.value)}
                                    </Td>
                                  ))}
                                  {showChange && (
                                    <Td isNumeric>
                                      <ChangeIndicator change={accountChange} />
                                    </Td>
                                  )}
                                </Tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}

                      {/* Direct accounts (for P&L flat structure or sections without subsections) */}
                      {hasAccounts && section.accounts.map((account, accountIdx) => {
                        // Reverse account months for display (oldest first)
                        const reversedAccountMonths = account.months ? [...account.months].reverse() : [];
                        const accountChange =
                          showChange && reversedAccountMonths.length >= 2
                            ? calculateChange(reversedAccountMonths[reversedAccountMonths.length - 1].value, reversedAccountMonths[reversedAccountMonths.length - 2].value)
                            : null;

                        return (
                          <Tr
                            key={`${section.id}-${account.label}-${accountIdx}`}
                            _hover={{ bg: hoverBg }}
                          >
                            <Td fontSize="sm" pl={10}>
                              {account.label}
                            </Td>
                            {reversedAccountMonths.map((monthData, mIdx) => (
                              <Td
                                key={monthData.month}
                                isNumeric
                                fontSize="sm"
                                color={monthData.value < 0 ? 'red.500' : mIdx === reversedAccountMonths.length - 1 ? 'inherit' : mutedColor}
                              >
                                {formatCurrency(monthData.value)}
                              </Td>
                            ))}
                            {showChange && (
                              <Td isNumeric>
                                <ChangeIndicator change={accountChange} />
                              </Td>
                            )}
                          </Tr>
                        );
                      })}
                      
                      {/* Show message if no content */}
                      {!hasSubsections && !hasAccounts && (
                        <Tr>
                          <Td colSpan={displayMonthLabels.length + (showChange ? 2 : 1)} textAlign="center" color={mutedColor} fontSize="sm" py={4}>
                            No data available for this section
                          </Td>
                        </Tr>
                      )}
                    </>
                  )}

                  {/* Gross Profit after COGS */}
                  {isPnl && section.id === 'cogs' && totals?.gross_profit && (() => {
                    const reversedGrossProfit = [...totals.gross_profit].reverse();
                    return (
                      <Tr bg={totalRowBg}>
                        <Td fontWeight="semibold" fontSize="sm" color={primaryColor}>
                          Gross Profit
                        </Td>
                        {reversedGrossProfit.map((monthData, mIdx) => (
                          <Td
                            key={monthData.month}
                            isNumeric
                            fontWeight="semibold"
                            fontSize="sm"
                            color={monthData.value < 0 ? 'red.500' : mIdx === reversedGrossProfit.length - 1 ? primaryColor : mutedColor}
                          >
                            {formatCurrency(monthData.value)}
                          </Td>
                        ))}
                        {showChange && (
                          <Td isNumeric>
                            <ChangeIndicator
                              change={
                                reversedGrossProfit.length >= 2
                                  ? calculateChange(reversedGrossProfit[reversedGrossProfit.length - 1].value, reversedGrossProfit[reversedGrossProfit.length - 2].value)
                                  : null
                              }
                            />
                          </Td>
                        )}
                      </Tr>
                    );
                  })()}

                  {/* Balance Sheet: Show total after last section of each class */}
                  {!isPnl && isLastOfClass && (
                    <>
                      {sectionClass === 'Asset' && totals?.total_assets && (() => {
                        const reversedTotalAssets = [...totals.total_assets].reverse();
                        return (
                          <Tr bg={totalRowBg}>
                            <Td fontWeight="semibold" fontSize="sm" color={primaryColor}>
                              Total Assets
                            </Td>
                            {reversedTotalAssets.map((monthData, mIdx) => (
                              <Td
                                key={monthData.month}
                                isNumeric
                                fontWeight="semibold"
                                fontSize="sm"
                                color={monthData.value < 0 ? 'red.500' : mIdx === reversedTotalAssets.length - 1 ? primaryColor : mutedColor}
                              >
                                {formatCurrency(monthData.value)}
                              </Td>
                            ))}
                            {showChange && (
                              <Td isNumeric>
                                <ChangeIndicator
                                  change={
                                    reversedTotalAssets.length >= 2
                                      ? calculateChange(reversedTotalAssets[reversedTotalAssets.length - 1].value, reversedTotalAssets[reversedTotalAssets.length - 2].value)
                                      : null
                                  }
                                />
                              </Td>
                            )}
                          </Tr>
                        );
                      })()}

                      {sectionClass === 'Liability' && totals?.total_liabilities && (() => {
                        const reversedTotalLiabilities = [...totals.total_liabilities].reverse();
                        return (
                          <Tr bg={totalRowBg}>
                            <Td fontWeight="semibold" fontSize="sm" color={primaryColor}>
                              Total Liabilities
                            </Td>
                            {reversedTotalLiabilities.map((monthData, mIdx) => (
                              <Td
                                key={monthData.month}
                                isNumeric
                                fontWeight="semibold"
                                fontSize="sm"
                                color={monthData.value < 0 ? 'red.500' : mIdx === reversedTotalLiabilities.length - 1 ? primaryColor : mutedColor}
                              >
                                {formatCurrency(monthData.value)}
                              </Td>
                            ))}
                            {showChange && (
                              <Td isNumeric>
                                <ChangeIndicator
                                  change={
                                    reversedTotalLiabilities.length >= 2
                                      ? calculateChange(reversedTotalLiabilities[reversedTotalLiabilities.length - 1].value, reversedTotalLiabilities[reversedTotalLiabilities.length - 2].value)
                                      : null
                                  }
                                />
                              </Td>
                            )}
                          </Tr>
                        );
                      })()}

                      {sectionClass === 'Equity' && totals?.total_equity && (() => {
                        const reversedTotalEquity = [...totals.total_equity].reverse();
                        return (
                          <Tr bg={totalRowBg}>
                            <Td fontWeight="semibold" fontSize="sm" color={primaryColor}>
                              Total Equity
                            </Td>
                            {reversedTotalEquity.map((monthData, mIdx) => (
                              <Td
                                key={monthData.month}
                                isNumeric
                                fontWeight="semibold"
                                fontSize="sm"
                                color={monthData.value < 0 ? 'red.500' : mIdx === reversedTotalEquity.length - 1 ? primaryColor : mutedColor}
                              >
                                {formatCurrency(monthData.value)}
                              </Td>
                            ))}
                            {showChange && (
                              <Td isNumeric>
                                <ChangeIndicator
                                  change={
                                    reversedTotalEquity.length >= 2
                                      ? calculateChange(reversedTotalEquity[reversedTotalEquity.length - 1].value, reversedTotalEquity[reversedTotalEquity.length - 2].value)
                                      : null
                                  }
                                />
                              </Td>
                            )}
                          </Tr>
                        );
                      })()}
                    </>
                  )}
                </React.Fragment>
              );
            })}

            {/* Net Profit Row for P&L */}
            {isPnl && totals?.net_profit && (() => {
              const reversedNetProfit = [...totals.net_profit].reverse();
              return (
                <Tr bg={totalRowBg}>
                  <Td fontWeight="semibold" fontSize="sm" color={primaryColor}>
                    Net Profit / (Loss)
                  </Td>
                  {reversedNetProfit.map((monthData, mIdx) => (
                    <Td
                      key={monthData.month}
                      isNumeric
                      fontWeight="semibold"
                      fontSize="sm"
                      color={monthData.value < 0 ? 'red.500' : mIdx === reversedNetProfit.length - 1 ? primaryColor : mutedColor}
                    >
                      {formatCurrency(monthData.value)}
                    </Td>
                  ))}
                  {showChange && (
                    <Td isNumeric>
                      <ChangeIndicator
                        change={
                          reversedNetProfit.length >= 2
                            ? calculateChange(reversedNetProfit[reversedNetProfit.length - 1].value, reversedNetProfit[reversedNetProfit.length - 2].value)
                            : null
                        }
                      />
                    </Td>
                  )}
                </Tr>
              );
            })()}
          </Tbody>
        </Table>
      </TableContainer>
    </Card>
  );
};

/**
 * Exports accounting data (P&L and Balance Sheet) to a CSV file
 * @param {Object} data - Accounting data object containing P&L and Balance Sheet sections
 * @param {Array<string>} monthLabels - Array of month labels for CSV column headers
 * @returns {void} Triggers a browser download of the CSV file
 * @description Creates a CSV file with all P&L and Balance Sheet data, including section headers,
 * account details, and monthly values. The file is automatically downloaded with timestamp.
 */
const exportToCSV = (data, monthLabels) => {
  if (!data) return;

  const rows = [];
  
  // Reverse monthLabels to show oldest first
  const reversedMonthLabels = [...monthLabels].reverse();
  
  // Add header
  rows.push(['Section', 'Account', ...reversedMonthLabels]);

  // Add P&L data
  if (data.pnl_sections && data.pnl_sections.length > 0) {
    rows.push(['PROFIT & LOSS', '', ...Array(reversedMonthLabels.length).fill('')]);
    data.pnl_sections.forEach(section => {
      rows.push([section.label || section.id, '', ...Array(reversedMonthLabels.length).fill('')]);
      if (section.accounts) {
        section.accounts.forEach(account => {
          // Reverse to show oldest first
          const values = account.months ? [...account.months].reverse().map(m => m.value ?? '') : [];
          rows.push(['', account.label, ...values]);
        });
      }
      if (section.total_months && section.total_months.length > 0) {
        // Reverse to show oldest first
        const totals = [...section.total_months].reverse().map(m => m.value ?? '');
        rows.push(['', `Total ${section.label}`, ...totals]);
      }
    });
    
    // Add gross profit
    if (data.gross_profit && data.gross_profit.length > 0) {
      // Reverse to show oldest first
      const grossValues = [...data.gross_profit].reverse().map(m => m.value ?? '');
      rows.push(['', 'GROSS PROFIT', ...grossValues]);
    }
    
    // Add net profit
    if (data.net_profit && data.net_profit.length > 0) {
      // Reverse to show oldest first
      const netValues = [...data.net_profit].reverse().map(m => m.value ?? '');
      rows.push(['', 'NET PROFIT', ...netValues]);
    }
  }

  // Add Balance Sheet data
  if (data.balance_sheet_sections && data.balance_sheet_sections.length > 0) {
    rows.push(['', '', ...Array(reversedMonthLabels.length).fill('')]);
    rows.push(['BALANCE SHEET', '', ...Array(reversedMonthLabels.length).fill('')]);
    data.balance_sheet_sections.forEach(section => {
      rows.push([section.label || section.id, '', ...Array(reversedMonthLabels.length).fill('')]);
      
      // Handle subsections (Balance Sheet hierarchical structure)
      if (section.subsections) {
        section.subsections.forEach(subsection => {
          rows.push(['', subsection.label, ...Array(reversedMonthLabels.length).fill('')]);
          if (subsection.accounts) {
            subsection.accounts.forEach(account => {
              // Reverse to show oldest first
              const values = account.months ? [...account.months].reverse().map(m => m.value ?? '') : [];
              rows.push(['', `  ${account.label}`, ...values]);
            });
          }
          if (subsection.total_months && subsection.total_months.length > 0) {
            // Reverse to show oldest first
            const totals = [...subsection.total_months].reverse().map(m => m.value ?? '');
            rows.push(['', `Total ${subsection.label}`, ...totals]);
          }
        });
      }
      
      // Handle direct accounts (if any)
      if (section.accounts) {
        section.accounts.forEach(account => {
          // Reverse to show oldest first
          const values = account.months ? [...account.months].reverse().map(m => m.value ?? '') : [];
          rows.push(['', account.label, ...values]);
        });
      }
      
      if (section.total_months && section.total_months.length > 0) {
        // Reverse to show oldest first
        const totals = [...section.total_months].reverse().map(m => m.value ?? '');
        rows.push(['', `Total ${section.label}`, ...totals]);
      }
    });

    // Add balance sheet totals
    if (data.total_assets && data.total_assets.length > 0) {
      // Reverse to show oldest first
      const assetValues = [...data.total_assets].reverse().map(m => m.value ?? '');
      rows.push(['', 'TOTAL ASSETS', ...assetValues]);
    }
    if (data.total_liabilities && data.total_liabilities.length > 0) {
      // Reverse to show oldest first
      const liabValues = [...data.total_liabilities].reverse().map(m => m.value ?? '');
      rows.push(['', 'TOTAL LIABILITIES', ...liabValues]);
    }
    if (data.total_equity && data.total_equity.length > 0) {
      // Reverse to show oldest first
      const equityValues = [...data.total_equity].reverse().map(m => m.value ?? '');
      rows.push(['', 'TOTAL EQUITY', ...equityValues]);
    }
  }

  // Convert to CSV
  const csvContent = rows.map(row => 
    row.map(cell => {
      // Escape quotes and wrap in quotes if contains comma
      const cellStr = String(cell);
      if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
        return `"${cellStr.replace(/"/g, '""')}"`;
      }
      return cellStr;
    }).join(',')
  ).join('\n');

  // Download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `accounting_data_${moment().format('YYYY-MM-DD')}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const Accounting = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [endDate, setEndDate] = useState(getDefaultAccountingEndDate());
  const [compareMonths, setCompareMonths] = useState(1);
  const [tabIndex, setTabIndex] = useState(1); // 0: Overview, 1: P&L, 2: Balance Sheet
  const [pnlExpandedSections, setPnlExpandedSections] = useState(new Set());
  const [bsExpandedSections, setBsExpandedSections] = useState(new Set());
  const [bsExpandedSubsections, setBsExpandedSubsections] = useState(new Set()); // Track expanded subsections
  const { customerId } = useParams();
  const toast = useToast();

  // Get section IDs
  const pnlSectionIds = useMemo(() => data?.pnl_sections?.map(s => s.id) || [], [data]);
  const bsSectionIds = useMemo(() => data?.balance_sheet_sections?.map(s => s.id) || [], [data]);

  // Toggle functions
  const togglePnlSection = (sectionId) => {
    setPnlExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const toggleBsSection = (sectionId) => {
    setBsExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const toggleBsSubsection = (sectionId, subsectionId) => {
    const key = `${sectionId}:${subsectionId}`;
    setBsExpandedSubsections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleAllPnlSections = (expand) => {
    if (expand) {
      setPnlExpandedSections(new Set(pnlSectionIds));
    } else {
      setPnlExpandedSections(new Set());
    }
  };

  const toggleAllBsSections = (expand) => {
    if (expand) {
      setBsExpandedSections(new Set(bsSectionIds));
      // Also expand all subsections
      const allSubsections = new Set();
      data?.balance_sheet_sections?.forEach(section => {
        section.subsections?.forEach(subsection => {
          allSubsections.add(`${section.id}:${subsection.id}`);
        });
      });
      setBsExpandedSubsections(allSubsections);
    } else {
      setBsExpandedSections(new Set());
      setBsExpandedSubsections(new Set());
    }
  };

  const allPnlExpanded = pnlSectionIds.every((id) => pnlExpandedSections.has(id)) && pnlSectionIds.length > 0;
  const allBsExpanded = bsSectionIds.every((id) => bsExpandedSections.has(id)) && bsSectionIds.length > 0;

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAccountingDataApi({
        customerId,
        endDate,
        compareMonths,
      });
      setData(response);
    } catch (error) {
      toast({
        title: 'Error fetching accounting data',
        description: error.message || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [customerId, endDate, compareMonths, toast]);

  useEffect(() => {
    if (customerId) {
      fetchData();
    }
  }, [customerId, fetchData]);

  const monthLabels = data?.month_labels ?? [];
  const noDataMessage = data?.no_data_message ?? '';
  const hasAnyData = data?.pnl_sections?.length > 0 || data?.balance_sheet_sections?.length > 0;
  const hasPnlData = data?.pnl_sections?.length > 0;
  const hasBsData = data?.balance_sheet_sections?.length > 0;

  const pnlTotals = useMemo(() => ({
    gross_profit: data?.gross_profit || [],
    net_profit: data?.net_profit || [],
  }), [data]);

  const bsTotals = useMemo(() => ({
    total_assets: data?.total_assets || [],
    total_liabilities: data?.total_liabilities || [],
    total_equity: data?.total_equity || [],
  }), [data]);

  return (
    <OverlaySpinnerWrapper loading={loading}>
      <Box minW={'container.lg'} maxW={'max'}>
        {/* Filters */}
        <Flex w={'100%'} justify={'space-between'} alignItems={'end'} mb={6}>
          <Flex gap={5} flexWrap="wrap">
            <Box w={200}>
              <FieldInput label="End date" id="accountingEndDate">
                <TwDatePicker
                  label="End date"
                  variant="white"
                  value={endDate}
                  onChange={setEndDate}
                  maxDate={moment().format('YYYY-MM-DD')}
                  inputProps={{ boxShadow: 'box', placeholder: 'End date' }}
                />
              </FieldInput>
            </Box>
            <Box w={140}>
              <FieldInput label="Compare months" id="compareMonths">
                <Select
                  value={compareMonths}
                  onChange={(e) => setCompareMonths(Number(e.target.value))}
                  boxShadow="box"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                    <option key={n} value={n}>
                      {n} month{n > 1 ? 's' : ''}
                    </option>
                  ))}
                </Select>
              </FieldInput>
            </Box>
          </Flex>
          <Flex gap={3}>
            {hasAnyData && (
              <Button
                size="sm"
                colorScheme="blue"
                onClick={() => exportToCSV(data, monthLabels)}
                leftIcon={<DownloadIcon />}
              >
                Export CSV
              </Button>
            )}
          </Flex>
        </Flex>

        {noDataMessage && (
          <Alert status={hasAnyData ? 'warning' : 'info'} borderRadius="md" mb={4}>
            <AlertIcon />
            <Box>
              <AlertTitle>{hasAnyData ? 'Partial Data' : 'No Data Available'}</AlertTitle>
              <AlertDescription>{noDataMessage}</AlertDescription>
            </Box>
          </Alert>
        )}

        {/* Tabs */}
        <Tabs index={tabIndex} onChange={setTabIndex} colorScheme="blue">
          <TabList>
            <Tab>Overview</Tab>
            <Tab>Profit & Loss</Tab>
            <Tab>Balance Sheet</Tab>
          </TabList>

          <TabPanels>
            {/* Overview Tab */}
            <TabPanel>
              <Text fontSize="lg" color="gray.600">
                Overview metrics coming soon...
              </Text>
            </TabPanel>

            {/* P&L Tab */}
            <TabPanel>
              {hasPnlData ? (
                <Box>
                  <Flex align="center" justify="space-between" mb={4}>
                    <Flex align="center" gap={2}>
                      <Checkbox
                        isChecked={allPnlExpanded}
                        onChange={(e) => toggleAllPnlSections(e.target.checked)}
                      />
                      <Text fontSize="sm" cursor="pointer" onClick={() => toggleAllPnlSections(!allPnlExpanded)}>
                        Expand all sections
                      </Text>
                    </Flex>
                    {data.pnl_last_tran_date && (
                      <Text fontSize="sm" color="gray.600">
                        Data updated through: <strong>{new Date(data.pnl_last_tran_date).toLocaleDateString()}</strong>
                      </Text>
                    )}
                  </Flex>
                  <FinancialTable
                    title="Profit & Loss Statement"
                    sections={data.pnl_sections}
                    monthLabels={monthLabels}
                    totals={pnlTotals}
                    expandedSections={pnlExpandedSections}
                    onToggleSection={togglePnlSection}
                    expandedSubsections={null}
                    onToggleSubsection={null}
                    isPnl={true}
                  />
                </Box>
              ) : (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>No P&L Data</AlertTitle>
                    <AlertDescription>Profit & Loss data is not available for this period.</AlertDescription>
                  </Box>
                </Alert>
              )}
            </TabPanel>

            {/* Balance Sheet Tab */}
            <TabPanel>
              {hasBsData ? (
                <Box>
                  <Flex align="center" justify="space-between" mb={4}>
                    <Flex align="center" gap={2}>
                      <Checkbox
                        isChecked={allBsExpanded}
                        onChange={(e) => toggleAllBsSections(e.target.checked)}
                      />
                      <Text fontSize="sm" cursor="pointer" onClick={() => toggleAllBsSections(!allBsExpanded)}>
                        Expand all sections
                      </Text>
                    </Flex>
                    {data.balance_sheet_last_tran_date && (
                      <Text fontSize="sm" color="gray.600">
                        Data updated through: <strong>{new Date(data.balance_sheet_last_tran_date).toLocaleDateString()}</strong>
                      </Text>
                    )}
                  </Flex>
                  <FinancialTable
                    title="Balance Sheet"
                    sections={data.balance_sheet_sections}
                    monthLabels={monthLabels}
                    totals={bsTotals}
                    expandedSections={bsExpandedSections}
                    onToggleSection={toggleBsSection}
                    expandedSubsections={bsExpandedSubsections}
                    onToggleSubsection={toggleBsSubsection}
                    isPnl={false}
                  />
                </Box>
              ) : (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>No Balance Sheet Data</AlertTitle>
                    <AlertDescription>Balance Sheet data is not available for this period.</AlertDescription>
                  </Box>
                </Alert>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </OverlaySpinnerWrapper>
  );
};

export default Accounting;
